import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const valueBadges = [
  { icon: "ri-brain-line", label: "AI Business Consulting" },
  { icon: "ri-robot-2-line", label: "Agentic Applications & Workflows" },
  { icon: "ri-sparkling-line", label: "Generative AI & Copilots" },
  { icon: "ri-global-line", label: "AI-Powered Websites & Chatbots" },
  { icon: "ri-graduation-cap-line", label: "IT Training & Global Certification" },
  { icon: "ri-map-pin-2-line", label: "Global Delivery | Chicago & Lagos" },
];

const words = ["Lead with AI.", "Transform with Intelligence.", "Build Agentic Systems.", "Scale with Confidence."];

export default function HeroSection() {
  const [wordIndex, setWordIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const [visibleItems, setVisibleItems] = useState<number[]>([]);
  const [checkedItems, setCheckedItems] = useState<number[]>([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setWordIndex((i) => (i + 1) % words.length);
        setFade(true);
      }, 400);
    }, 3200);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    valueBadges.forEach((_, i) => {
      const slideDelay = 800 + i * 180;
      const checkDelay = slideDelay + 300;
      const slideTimer = setTimeout(() => {
        setVisibleItems((prev) => [...prev, i]);
      }, slideDelay);
      const checkTimer = setTimeout(() => {
        setCheckedItems((prev) => [...prev, i]);
      }, checkDelay);
      return () => {
        clearTimeout(slideTimer);
        clearTimeout(checkTimer);
      };
    });
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden bg-white">
      {/* Subtle light background pattern */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=very%20light%20minimal%20white%20abstract%20geometric%20hexagonal%20grid%20pattern%20pale%20gray%20lines%20on%20white%20background%20subtle%20tech%20texture%20clean%20modern%20enterprise%20surface%20ultra%20wide&width=1440&height=900&seq=cpihero-light-v2&orientation=landscape"
          alt="CPI Technologies hero background"
          className="w-full h-full object-cover object-center opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-white/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-white/90 via-transparent to-transparent" />
      </div>

      {/* Lime green accent blob top-right */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#C8F135]/15 blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 right-20 w-[300px] h-[300px] rounded-full bg-[#C8F135]/10 blur-2xl pointer-events-none" />

      {/* Main content */}
      <div className="relative z-10 flex-1 flex items-center w-full max-w-7xl mx-auto px-4 md:px-8 pt-28 md:pt-32 pb-20 md:pb-28">
        <div className="max-w-3xl w-full">
          {/* Badges row */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-6">
            <div className="inline-flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-full px-4 py-2">
              <span className="w-2 h-2 rounded-full bg-[#C8F135] flex-shrink-0 animate-pulse" />
              <span className="text-slate-600 text-xs font-medium whitespace-nowrap">AI-First Global Consulting</span>
            </div>
            <div className="inline-flex items-center gap-2 bg-[#C8F135]/20 border border-[#C8F135]/50 rounded-full px-4 py-1.5">
              <i className="ri-building-line text-[#9bc400] text-sm" />
              <span className="text-[#6a7a00] text-xs font-semibold uppercase tracking-wider">Chicago &amp; Lagos · Founded 1996</span>
            </div>
          </div>

          {/* Animated Headline */}
          <div className="mb-4 overflow-hidden">
            <div className={`text-4xl sm:text-5xl md:text-7xl font-black leading-tight transition-all duration-400 ${fade ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <span className="text-slate-900">{words[wordIndex]}</span>
            </div>
          </div>

          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold leading-tight mb-6" style={{ color: "#9bc400" }}>
            Your AI Business Transformation Partner.
          </h2>

          <p className="text-slate-700 text-base md:text-xl leading-relaxed mb-4 max-w-2xl">
            CPI Technologies Inc helps organizations move beyond AI hype to real-world impact.
          </p>
          <p className="text-slate-500 text-sm md:text-base leading-relaxed mb-8 max-w-xl">
            From adoption strategy to agentic applications, AI-powered websites, intelligent chatbots, and enterprise-grade automation.
          </p>

          {/* Value Prop Checklist */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mb-8">
            {valueBadges.map((b, i) => {
              const isVisible = visibleItems.includes(i);
              const isChecked = checkedItems.includes(i);
              return (
                <div
                  key={b.label}
                  className={`flex items-center gap-2.5 transition-all duration-500 ${
                    isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
                  }`}
                >
                  <div
                    className={`w-5 h-5 flex items-center justify-center rounded-full border flex-shrink-0 transition-all duration-300 ${
                      isChecked
                        ? "bg-[#C8F135] border-[#C8F135] scale-100"
                        : "bg-transparent border-slate-300 scale-75"
                    }`}
                  >
                    <i
                      className={`ri-check-line text-black text-xs transition-all duration-200 ${
                        isChecked ? "opacity-100 scale-100" : "opacity-0 scale-50"
                      }`}
                    />
                  </div>
                  <span
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isChecked ? "text-slate-800" : "text-slate-400"
                    }`}
                  >
                    {b.label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 mb-10">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 md:px-8 md:py-4 rounded-lg bg-[#C8F135] text-black font-bold text-sm md:text-base whitespace-nowrap cursor-pointer hover:bg-[#b5e000] transition-colors"
            >
              <i className="ri-calendar-check-line" /> Book a Free AI Consultation
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 md:px-8 md:py-4 rounded-lg border-2 border-black text-black font-semibold text-sm md:text-base whitespace-nowrap cursor-pointer hover:bg-black hover:text-[#C8F135] transition-colors"
            >
              <i className="ri-rocket-line" /> Start Your AI Transformation
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-6 sm:gap-8">
            {[
              { value: "28+", label: "Years Experience" },
              { value: "500+", label: "Projects Delivered" },
              { value: "98%", label: "Client Satisfaction" },
              { value: "2", label: "Global Offices" },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-2xl md:text-3xl font-black text-slate-900">{s.value}</div>
                <div className="text-slate-400 text-xs md:text-sm mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Partner logos strip */}
      <div className="relative z-10 border-t border-slate-200 bg-slate-50 py-4 px-4 md:px-8">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-4 md:gap-10">
          <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Global Partners:</span>
          {["Microsoft", "AWS", "Google Cloud", "i3Cert"].map((p) => (
            <span key={p} className="text-slate-500 text-sm font-bold hover:text-[#9bc400] transition-colors whitespace-nowrap">{p}</span>
          ))}
        </div>
      </div>
    </section>
  );
}
